// The Hill saturation curve, as a set of knobs.
//
//   y = Vmax * x^n / (K^n + x^n)
//
// This is the shape the platform already assumes its saturation curves have: the "Export curves"
// button in SaturationCurves.vue fits exactly this function and writes the three parameters out as
// carry_cap (Vmax), shape_u (K) and shape_b (n). The EKRM wizards reuse the same fitter, so the
// numbers under their sliders are the numbers that export would produce.
//
// Everything here is pure maths with no knowledge of model reports, which is what lets
// ekrmFromModel.js depend on it without a cycle.
//
// What each parameter means to someone moving a slider:
//   Vmax  the ceiling — the most revenue the channel can ever return, however much is spent
//   K     the spend at which the channel returns half that ceiling: where the knee sits
//   n     how sharply the curve turns; below 1 it is concave from the origin, above 1 it is an S

import { fitSaturationCurve } from '@/mixins/saturationCurves.js'
import { regionsStrictlyIncreasing } from '@/utils/regions.js'

// Same guard value the mixin's own generator uses, so hillAt and the fitted function agree.
const EPSILON = 1e-9

// Above this the fit is not trusted to stand in for the source curve and the import keeps the
// values it sampled. Expressed as a percentage of the ceiling, so it means the same thing for a
// channel worth thousands and one worth millions.
export const HILL_FIT_TOLERANCE = 5

export function isHillParams (params) {
  return !!params && isFinite(params.vmax) && isFinite(params.k) && isFinite(params.n) &&
    params.vmax > 0 && params.k > 0 && params.n > 0
}

// Deliberately mirrors hillFunctionGenerator in src/mixins/saturationCurves.js, including its
// clamp of x to EPSILON rather than a special case at zero, so a fitted curve and this evaluation
// cannot disagree anywhere.
export function hillAt (params, x) {
  if (!isHillParams(params)) return 0
  const kSafe = Math.max(params.k, EPSILON)
  const xSafe = Math.max(Number(x), EPSILON)
  const kPowN = Math.pow(kSafe, params.n)
  const xPowN = Math.pow(xSafe, params.n)
  if (!isFinite(kPowN) || !isFinite(xPowN)) return 0
  const denominator = kPowN + xPowN
  if (denominator === 0 || !isFinite(denominator)) return 0
  const result = params.vmax * xPowN / denominator
  return isFinite(result) ? result : 0
}

// The ratio between one grid column and the next. Ten columns evenly spaced from a tenth of the
// saturation point to 1.2 times it — which is what this grid was until now, and what the old
// wizard's jspreadsheet formulas still compute — cannot describe an S-shaped curve: the columns
// below the knee are the ones that carry the shape, and an even ladder puts only one to three of
// the ten there while the remaining seven to nine repeat the plateau. A geometric ladder spends
// its columns where the curve is actually moving.
//
// 1.8 spans 198 times the first column, which is what it takes to hold both ends of the curve at
// once now that curveGridPoint runs the grid out to the ceiling rather than to the first position
// that satisfies the convergence rule: the tenth column sits dozens of knees out and two to five of
// the ten still land under the knee. At 1.4 the same placement pushes every column above the knee,
// and the EKRM's refit comes back with the knee 6% too far out for want of anything below it.
export const GRID_RATIO = 1.8

// The ten x positions the EKRM grid asks about: ten geometric steps ending at the saturation point
// stretched by 1.2. The last column is where it always was, so the saturation point keeps its
// meaning to everything that reads it — the validator's spend-band check and the number under the
// first slider. The same positions buildEkrmDefinition regenerates for the
// payload's `spend` array and syncMediaGrids renders as formulas off column D.
export function gridSpends (point) {
  const base = isFinite(point) && point > 0 ? point : 1
  const spends = []
  for (let k = 1; k <= 10; k++) {
    spends.push(base * 1.2 * Math.pow(GRID_RATIO, k - 10))
  }
  return spends
}

// The ten revenue values the grid stores. Strictly increasing for any n > 0, which is what makes
// the wizard's "decreasing saturation" hard error unreachable from the sliders.
export function hillSamples (params, point) {
  return gridSpends(point).map(function (x) { return hillAt(params, x) })
}

// WHERE THE CURVE SATURATES: the spend at which the response reaches nine tenths of the ceiling,
// which is the report's own fourth boundary and the end of its optimal zone.
//
// Its own export because it is the one of the four that is defined for EVERY exponent — the two
// interior boundaries need an interior peak and so need n above one — and because it is what places
// a curve in dollars. It is a property of the two parameters and of nothing else: not of how far a
// grid happened to run, and not of how much of its own curve some business happened to buy.
export function hillDiminishingReturns (params) {
  if (!isHillParams(params)) return 0
  return params.k * Math.pow(9, 1 / params.n)
}

// THE FOUR BOUNDARIES OF THE FIVE OPTIMAL ZONES, off the curve's own three parameters.
//
// The definitions are the report backend's, from NgReport.saturation_regions: the THRESHOLD is where
// the response reaches a tenth of what the curve comes to, MAX MARGINAL is where the derivative
// peaks, MAX ROI is where the return per dollar S(x)/x peaks, and DIMINISHING RETURNS is where the
// response reaches nine tenths. The backend reads all four off a grid and keeps the nearest column;
// a Hill has a closed form for every one of them, so they are solved here instead — which is what
// lets them follow a lever continuously rather than in ten steps, and what makes them a function of
// the parameters alone rather than of an imported array.
//
//   S(x) = V·xⁿ / (xⁿ + kⁿ)
//   S(x) = qV        →  x = k·(q/(1-q))^(1/n)     with q a tenth and nine tenths
//   S'(x) maximal    →  x = k·((n-1)/(n+1))^(1/n)
//   S(x)/x maximal   →  x = k·(n-1)^(1/n)
//
// The reference for the two quantiles is the CEILING and not the end of a grid. The backend's is
// whatever its input range came to; the grid this wizard writes is laid out to reach CEILING_TARGET
// of the ceiling, so within half a percent the two are the same number — and the ceiling is a
// property of the three parameters, which the end of a grid is not.
//
// Both interior points need n > 1: below it the derivative and the return per dollar are each
// greatest as the spend approaches nothing, there is no interior peak, and the zones lose their
// meaning. Between 1 and 1.25 the threshold overtakes the peak derivative — (1/9) < (n-1)/(n+1)
// only above 1.25, which is exactly the bound the backend's own comment names — and the answer here
// is the backend's answer: halve the peak derivative.
//
// Null unless the four strictly ascend, which is the condition every chart shades on.
export function hillRegions (params) {
  if (!isHillParams(params) || !(params.n > 1)) return null
  const n = params.n
  const at = function (ratio) { return params.k * Math.pow(ratio, 1 / n) }
  const maxMarginal = at((n - 1) / (n + 1))
  const maxRoi = at(n - 1)
  const diminishing = hillDiminishingReturns(params)
  let threshold = at(1 / 9)
  if (threshold >= maxMarginal) threshold = maxMarginal / 2
  const regions = [threshold, maxMarginal, maxRoi, diminishing]
  if (regions.filter(isFinite).length !== 4 || !(regions[0] > 0)) return null
  return regionsStrictlyIncreasing(regions) ? regions : null
}

// The exponents a zone boundary is solved over, and how finely. The scan is for the interval the
// answer is in and the bisection is inside it: hillRegions is monotone in n but it is not smooth —
// it steps down by half at the exponent where the threshold overtakes the peak derivative — so a
// Newton step off a derivative that does not exist there would walk out of the interval it started
// in. Two hundred and forty columns put that step well inside one column, and forty passes take
// the answer to about a part in ten trillion of the range, which is far below a pixel of a bar.
const REGION_SCAN = 240
const REGION_PASSES = 40

// How near the target a bisected interval has to come to be an answer rather than a jump the
// boundary stepped over. Forty passes take a real root far below this; a jump cannot be brought
// inside it at all.
const REGION_TOLERANCE = 1e-6

// Where the exponent may go while a boundary is dragged. Below one there are no zones at all —
// hillRegions is null, the bands are not drawn and there is nothing to have dragged — and above
// ten the peak return per dollar has passed the diminishing-returns point, which is the same
// answer by another route. Both are outside the range the levers offer.
const REGION_N_MIN = 1.001
const REGION_N_MAX = 9.5

// The two things a dragged boundary can hold still, spelled once for the editor that offers the
// choice and for the tests that pin what each of them means. See hillRegionSet.
export const HILL_HOLD_SATURATION = 'saturation'
export const HILL_HOLD_SHAPE = 'shape'
export const HILL_HOLD_EDGE = 'edge'

// WHAT IS HELD STILL WHEN A BOUNDARY IS MOVED, which is the whole of what there is to choose.
//
// The four boundaries are read off TWO free numbers — k and n, the ceiling being a scale on the
// other axis that none of them touches — so THREE of them cannot be held while the fourth is
// dragged: that is four equations in two unknowns and it has no answer inside this family. And it
// is worse than that, which is worth stating once because it is what a reader asking for it is
// really asking: holding TWO already fixes the curve entire. Every ratio between two boundaries is
// a function of n alone, so two boundaries held name the exponent, and either of them then names
// k — leaving nothing to have dragged. EXACTLY ONE thing can stand still, and this is the list of
// what it may be.
//
//   b1 = k·9^(-1/n)   b2 = k·((n-1)/(n+1))^(1/n)   b3 = k·(n-1)^(1/n)   b4 = k·9^(1/n)
//
// HILL_HOLD_SATURATION holds the fourth boundary, which is the one this wizard states a channel in
// dollars with: "Saturates at" IS b4, and the first three describe the shape of the way there. So
// a boundary dragged under it is a move of the exponent with k following, the two headline figures
// stand exactly where they were, and only the picture of the way there changes.
//
// HILL_HOLD_SHAPE holds the exponent, which is every ratio between the four: b_i = k·f_i(n), so
// with n standing still a boundary put where it was dragged multiplies ALL FOUR by one factor. The
// bar's five widths come out identical, the saturation point travels with them, and the edit is
// the placement — the same edit as typing the figure beside the chart, whichever grip was taken
// hold of. It is the convention for moving the zones somewhere else; the other is the one for
// changing their shape.
//
// HILL_HOLD_EDGE holds the boundary opts.pin NAMES, and it is the general case the first of these
// is one setting of: pinned on b4 it is HILL_HOLD_SATURATION exactly, which is why the two run
// through one piece of arithmetic below. It is the nearest thing there is to "move this one and
// leave that one where it is" — the other two still move, there being only one thing to hold.
//
// NOT EVERY PIN IS INVERTIBLE, and the ones that are not are offered anyway. Under a pin the
// dragged boundary travels as b_i/b_p, again a function of n alone, and the pair (b1, b2) turns
// over: b2/b1 = (9(n-1)/(n+1))^(1/n) climbs to about 1.74 at n ≈ 2.15 and comes back down, and
// below n = 1.25 it is exactly 2. So a b2 asked to stand a distance from a held b1 that no exponent
// produces has no answer, and a b1 dragged against a held b2 has TWO, where the answer taken is the
// one nearest the exponent the curve already stands at, so that a small drag stays a small move.
// Every other pair is monotone across the exponents this can reach.
//
// WHAT HAS NO ANSWER IS REFUSED, and this is the one place this editor does not do what its levers
// do with a value they cannot take. A lever that runs out of range lands on the end of it, which is
// the nearest thing to what was asked; a boundary that runs out of EXPONENT does not, because the
// end of the exponent range is not near anything. The interior boundaries stand in a fixed ratio to
// k and the saturation point does not — b4 = k·9^(1/n) against b2 = k·((n-1)/(n+1))^(1/n) — so an
// exponent walked to the bottom of its range with an interior boundary pinned throws the saturation
// point out by orders of magnitude, and answers a drag of a quarter of an inch with a channel that
// saturates a thousand times further out. Null instead: the boundary stops where the shape can no
// longer follow it, the curve on the screen stays the last one that was actually asked for, and
// nothing else is thrown anywhere. A figure TYPED that has no answer is refused the same way, which
// is what the two fields beside the chart do with a figure they cannot take.
//
// The solve is ONE scan and one bisection for every pin, and it reads the boundaries off
// hillRegions ITSELF rather than off the closed forms above: the bands being dragged and the
// answer are then the same function by construction, rather than two spellings that have to be
// kept in agreement. Which matters here — hillRegions is not smooth, it steps down by half in the
// band below n = 1.25 where the threshold overtakes the peak derivative — and it is why the scan
// looks for a SIGN CHANGE rather than assuming which way the dragged boundary runs.
//
// Null, then, whenever there is nothing to answer with: no zones on this curve, no saturation
// point, a target that is not a positive amount, a boundary asked to be held and dragged at the
// same time, or a place on the spend axis no exponent can put this boundary.
export function hillRegionSet (params, index, value, options) {
  const target = Number(value)
  if (!isHillParams(params) || !isFinite(target) || target <= 0) return null
  if (!(index >= 0 && index <= 3)) return null
  const at = hillDiminishingReturns(params)
  if (!(at > 0)) return null
  const opts = options || {}
  const hold = opts.hold

  // A boundary put where it was dragged by SLIDING the whole curve along the spend axis: every
  // boundary is k times a function of n alone, so one factor on k moves all four by it and leaves
  // the shape — and the response the curve comes to — untouched.
  const slid = function (from) {
    return from > 0 ? { vmax: params.vmax, k: params.k * target / from, n: params.n } : null
  }

  // The fourth boundary is the placement under both conventions that hold no OTHER boundary, and
  // it is the same arithmetic both times: where a channel saturates is what puts its whole response
  // in dollars, and holding it still while dragging it is not a thing that can be asked for. Under
  // a pin it is a boundary like the other three and is solved with them.
  if (index === 3 && hold !== HILL_HOLD_EDGE) return slid(at)

  // The three interior boundaries have to EXIST to have been dragged or to be held: below an
  // exponent of one there is no interior peak, no zones are drawn and there is nothing under the
  // pointer. The saturation point is defined for every exponent, which is why it is answered above
  // this.
  const regions = hillRegions(params)
  if (regions === null) return null

  if (hold === HILL_HOLD_SHAPE) return slid(regions[index])

  // WHICH boundary stands still: the one named, or the fourth. What cannot be asked is for one
  // boundary to be both the thing held and the thing moved.
  const pin = hold === HILL_HOLD_EDGE ? Math.round(Number(opts.pin)) : 3
  if (!(pin >= 0 && pin <= 3) || pin === index) return null
  const anchor = regions[pin]
  if (!(anchor > 0)) return null

  const low = Math.max(REGION_N_MIN, isFinite(opts.nMin) ? opts.nMin : 0)
  const high = Math.min(REGION_N_MAX, isFinite(opts.nMax) ? opts.nMax : REGION_N_MAX)
  if (!(high > low)) return null

  // The four boundaries of the curve whose k is one, which is the four functions of n themselves.
  // The held boundary then names k — it is the anchor divided by its own shape — and every other
  // boundary is read as the anchor times the ratio of two of them, which is the quantity that has
  // an exponent to be solved for.
  const shapeAt = function (n) {
    return hillRegions({ vmax: params.vmax, k: 1, n: n })
  }
  const family = function (n) {
    const shape = shapeAt(n)
    return shape ? { vmax: params.vmax, k: anchor / shape[pin], n: n } : null
  }
  const boundary = function (n) {
    const shape = shapeAt(n)
    return shape ? anchor * shape[index] / shape[pin] : null
  }

  // Geometrically spaced, the boundaries being geometric in n: an even ladder spends its columns
  // where the curve has stopped moving. EVERY pair of columns the dragged boundary crosses the
  // target between is kept, because there can be more than one: the ratio of two boundaries turns
  // over for the pair (b1, b2), and it JUMPS at n = 1.25, where the threshold stops being a tenth
  // of the ceiling and becomes half of a peak derivative it has overtaken.
  const step = Math.pow(high / low, 1 / REGION_SCAN)
  const spans = []
  let lastN = null
  let lastGap = null
  for (let i = 0; i <= REGION_SCAN; i++) {
    const n = i === REGION_SCAN ? high : low * Math.pow(step, i)
    const found = boundary(n)
    if (found === null || !(found > 0)) continue
    if (found === target) return family(n)
    const gap = found - target
    if (lastGap !== null && (gap > 0) !== (lastGap > 0)) {
      spans.push({ lo: lastN, hi: n, below: lastGap < 0 })
    }
    lastN = n
    lastGap = gap
  }
  if (!spans.length) return null

  // NEAREST THE EXPONENT THE CURVE IS AT, and tried in that order, which is what makes a small drag
  // a small move: where a boundary can be put back on the target by two different shapes, the one
  // being asked for is the one beside the shape on the screen. And an interval whose bisection does
  // not arrive at the target was the jump and not a root — the boundary steps ACROSS the value that
  // was asked for there without ever taking it — so it is passed over for the next one out, and if
  // none of them arrives there was no exponent to be had.
  const from = Math.min(high, Math.max(low, params.n))
  spans.sort(function (a, b) {
    return Math.abs(Math.log((a.lo + a.hi) / (2 * from))) - Math.abs(Math.log((b.lo + b.hi) / (2 * from)))
  })

  for (let s = 0; s < spans.length; s++) {
    let lo = spans[s].lo
    let hi = spans[s].hi
    for (let pass = 0; pass < REGION_PASSES; pass++) {
      const mid = (lo + hi) / 2
      const found = boundary(mid)
      if (found === null) break
      if ((found < target) === spans[s].below) lo = mid
      else hi = mid
    }
    const n = (lo + hi) / 2
    const found = boundary(n)
    if (found === null || !(found > 0)) continue
    if (Math.abs(Math.log(found / target)) < REGION_TOLERANCE) return family(n)
  }
  return null
}

// THE X WINDOW A CURVE NEEDS FOR ITS OWN RESPONSE TO FIT INSIDE IT, both axes being the same unit.
//
// A saturation plot is dollars against dollars — what the channel is given and what it gives back —
// and scaling the two axes independently lays two rulers of different lengths on one quantity: a
// channel that returns half what it costs and one that returns three times it draw the SAME shape,
// each filling its own box, and neither picture says which of them it is. On one range they are told
// apart without reading a number off either axis, the height of the curve against the width of the
// plot being the return itself.
//
// It is the WINDOW that is raised to meet the response and never the response cropped to fit the
// window: a plot that cuts the top off a curve is a plot of a different curve. The iteration is
// x → hillAt(x) and it can only converge — hillAt is increasing, concave and strictly bounded by
// vmax, so vmax is above every fixed point and is the answer when the passes run out. A window
// already taller than the response inside it is handed straight back, which is the ordinary case:
// a channel that saturates before it has paid for itself needs no extra room.
export function hillLevelWindow (params, floor) {
  let top = Number(floor) > 0 ? Number(floor) : 0
  if (!isHillParams(params) || !(top > 0)) return top
  for (let pass = 0; pass < 40; pass++) {
    const reach = hillAt(params, top)
    if (!(reach > top * 1.0001)) return top
    top = reach
  }
  return Math.max(top, params.vmax)
}

// THE X WINDOW A SATURATION PLOT IS DRAWN OVER, wherever one is drawn. One rule, because the
// thumbnail on a channel's card and the chart the card opens are two sizes of one picture: a curve
// drawn to a different edge in each of them is a different SHAPE in each of them, and the reader is
// left comparing a card against a dialog that disagree about a channel neither of them has changed.
//
// Every term is a thing that has to be inside the box:
//   k * 4                 enough of a Hill to see that it turns at all, which is the floor for a
//                         channel carrying no solvable set of zones
//   saturation * 1.15     the one place on the curve this wizard is ABOUT, with room to its right,
//                         a point drawn against the right margin being a point half off the plot
//   regions[3] * 1.05     a little past the last boundary, so the diminishing-returns band is a
//                         band and not an edge at the margin
//   1                     a window is not nothing, for a curve whose parameters are all placeholders
//
// AND NOT WHAT IS SPENT ON THE CHANNEL. The window used to hold the plan's own level too, on the
// grounds that it is a place the built model will really be asked about — but a saturation curve is
// a statement about the MEDIUM and the market it is bought in, and what a plan later decides to put
// into it is a different claim answered on a different step. With the level in the window, typing a
// budget zoomed every card on the media step and the chart inside the editor with it, so a picture
// nobody had touched changed shape under an answer that has nothing to do with the shape. Every term
// left is a property of the three parameters and of nothing else, which is what makes these plots
// comparable at all: two cards side by side are two curves read against one rule.
//
// AND THEN WIDE ENOUGH TO HOLD ITS OWN RESPONSE. Both axes are the same unit — dollars against
// dollars — so a plot that puts them on one range has its x running to whichever of the two maxima
// is larger, and a curve climbing higher than its window is wide would leave the line stopping in
// the middle of the box with the axis running on past it. hillLevelWindow raises the window instead
// of cropping the curve. `source` is a second set of parameters drawn in the same box — the fit a
// channel was imported with, under the one that replaced it — and it is measured here for the same
// reason, so the two lines reach the same edge.
//
// options: { source, saturation }. Zero for parameters that are not a Hill.
export function hillPlotWindow (params, options) {
  if (!isHillParams(params)) return 0
  const opts = options || {}
  const at = Number(opts.saturation) > 0 ? Number(opts.saturation) : hillDiminishingReturns(params)
  const bands = hillRegions(params)
  const asked = Math.max(
    params.k * 4,
    at > 0 ? at * 1.15 : 0,
    bands ? bands[3] * 1.05 : 0,
    1
  )
  return Math.max(
    hillLevelWindow(params, asked),
    isHillParams(opts.source) ? hillLevelWindow(opts.source, asked) : 0
  )
}

// A polyline dense enough to read as a curve rather than as ten segments.
export function hillSeries (params, maxX, steps) {
  const count = steps && steps > 1 ? steps : 60
  const top = isFinite(maxX) && maxX > 0 ? maxX : 1
  const x = []
  const y = []
  for (let i = 0; i <= count; i++) {
    const at = top * i / count
    x.push(at)
    y.push(i === 0 ? 0 : hillAt(params, at))
  }
  return { x: x, y: y }
}

// How close the last two of the ten grid values have to be for the wizard to call the curve
// flattened. validateEkrmForm warns above it (src/utils/ekrmDefinition.js:566).
const GRID_CONVERGENCE_TOLERANCE = 0.03

// The smallest saturation point whose grid satisfies that rule for this curve. For a Hill function
// the rule is a condition on where the grid ends relative to the knee, so it can be solved rather
// than searched. The last two positions are 1.2*point/GRID_RATIO and 1.2*point, so with
// u = 1.2*point/K and a = GRID_RATIO^-n the ratio between them is a(1 + u^n)/(1 + a u^n), and
// asking for it to exceed 1 - tol gives
//
//   u^n > (1 - tol - a) / (tol * a)
//
// A curve with n below about 0.047 has a >= 1 - tol: it climbs by less than the tolerance over the
// last step of ANY grid, so there is nothing to bound and the answer is zero. Everywhere else u
// peaks near five hundred and thirty around n = 0.22, which puts this bound at about four hundred
// and forty times the knee — far above KNEE_GRID_LIMIT, which is why curveGridPoint holds it under
// that cap rather than assuming the interval is ordered.
//
// Solved a tenth inside the tolerance rather than exactly on it. validateEkrmForm warns when the
// last two values are 3% apart OR MORE, so a point that puts them at exactly 3% is as likely to
// trip the warning as to clear it once the two have been rounded into their cells — which is what
// every channel did before the margin was added.
export function convergencePoint (params) {
  if (!isHillParams(params)) return 0
  const tolerance = GRID_CONVERGENCE_TOLERANCE * 0.9
  const a = Math.pow(1 / GRID_RATIO, params.n)
  if (!isFinite(a) || a <= 0 || a >= 1 - tolerance) return 0
  const t = (1 - tolerance - a) / (tolerance * a)
  const u = Math.pow(t, 1 / params.n)
  if (!isFinite(u) || u <= 0) return 0
  return params.k * u / 1.2
}

// Past this multiple of the knee the first of the ten grid points, at 1.2/GRID_RATIO^9 of the
// saturation point, already sits at the half-saturation spend: everything below the knee falls off
// the bottom of the grid and what the ten values describe is a step rather than a curve. At a ratio
// of 1.8 that is 165 knees.
export const KNEE_GRID_LIMIT = Math.pow(GRID_RATIO, 9) / 1.2

// How much of the ceiling the last of the ten values has to have reached.
//
// This is the number the EKRM's refit turns out to hinge on. interpolate_saturation puts every
// channel on ONE shared spend index — the union of all of them — and forward-fills each column flat
// beyond its own last point, so whatever the tenth value is, the builder is told the response holds
// there for the rest of the index and fits its Hill through a plateau that is mostly fabricated. A
// grid that merely satisfies the three-percent rule stops at about 95% of the ceiling, and the
// clone comes back with a ceiling near 95%, a knee pulled in to match and an exponent raised to
// bend the curve early enough to reach it. Handing over the last half a percent instead costs
// nothing on this side and lands the refit within a percent of the curve it was copied from.
export const CEILING_TARGET = 0.995

// The saturation point whose grid ends where the curve has reached `fraction` of its ceiling:
// y/Vmax = f at x = K*(f/(1-f))^(1/n), and the grid ends at 1.2 times the point.
export function ceilingPoint (params, fraction) {
  if (!isHillParams(params)) return 0
  const f = isFinite(fraction) && fraction > 0 && fraction < 1 ? fraction : CEILING_TARGET
  const point = params.k * Math.pow(f / (1 - f), 1 / params.n) / 1.2
  return isFinite(point) && point > 0 ? point : 0
}

// Where to put the saturation point when the curve, rather than the planned spend, is what the grid
// has to describe: far enough out that the ten values carry the ceiling — past ceilingPoint, and
// past convergencePoint, which is the stricter of the two above n of about 3 and is what keeps the
// wizard from warning that the curve is still climbing — but never past KNEE_GRID_LIMIT * K, where
// the whole climb has fallen off the bottom of the grid.
//
// The cap wins when the two disagree, which they do below n of about 1: reaching 99.5% of the
// ceiling takes 199^(1/n) knees, and only an exponent at or above 1.0006 asks for fewer than the
// 1.8^9 the ladder spans. An n of 0.5 would need 39 601 of them, and a grid showing nothing but
// plateau describes the channel worse than one that stops half a percent short. At the cap the
// first column sits exactly on the knee, which is what KNEE_GRID_LIMIT means.
export function curveGridPoint (params, rawPoint) {
  const raw = isFinite(rawPoint) && rawPoint > 0 ? rawPoint : 0
  if (!isHillParams(params)) return raw
  const high = params.k * KNEE_GRID_LIMIT
  const low = Math.min(Math.max(convergencePoint(params), ceilingPoint(params, CEILING_TARGET)), high)
  if (raw <= 0) return low > 0 ? low : high
  return Math.min(Math.max(raw, low), high)
}

// The most points a replaced curve may carry. The source curves a report holds run to about fifty,
// and the ladder below only ever adds a tail, so this is a runaway guard rather than a budget.
const MAX_CURVE_POINTS = 200

// The x/y an EDITED channel travels with, and the only place in this project where a payload curve
// is anything other than the source model's own points.
//
// A wizard that imports curves cannot answer a moved lever the way a hand-built form does — drop
// the curve and let the ten grid values carry the parameters — because the backend's detection is
// ALL OR NOTHING: one medium without an x/y sends every medium down the expert-grid path, which
// under CURVE_PRIORITY puts the knee a hundred times above the plan. So an edit REPLACES the
// curve instead, and this is the replacement.
//
// The source curve's own x positions are kept, so the axis the payload already used does not move
// and an edit made at the fitted parameters is a change of y alone. The tail is then extended in
// GRID_RATIO steps until it reaches the spend at which the edited curve stands at CEILING_TARGET of
// its ceiling: a knee dragged outward otherwise leaves a curve that stops before its own ceiling,
// and a curve is read as FLAT past its last point by responseAt on this side and by the refit on
// the other — which would cap the channel at whatever the source model happened to explore, without
// saying so.
//
// Null on the same condition curvePayload is null on, so a medium that could not send a curve
// before an edit cannot send one after it either.
export function hillCurvePoints (params, sourceCurve) {
  if (!isHillParams(params)) return null

  // Strictly increasing and finite, which is what the other side requires and what the ladder below
  // assumes. The source has already been through numericPairs, so this drops nothing in practice.
  const source = sourceCurve && Array.isArray(sourceCurve.x) ? sourceCurve.x : []
  const x = []
  for (let i = 0; i < source.length; i++) {
    const at = Number(source[i])
    if (!isFinite(at) || at < 0) continue
    if (x.length === 0 || at > x[x.length - 1]) x.push(at)
  }
  if (x.length === 0) x.push(0)

  const reach = ceilingPoint(params, CEILING_TARGET) * 1.2
  let last = x[x.length - 1]
  // A source whose only point is the origin cannot be stepped from, so the ladder is seeded off the
  // knee at the same offset the ten grid columns start at.
  let next = last > 0 ? last : params.k / Math.pow(GRID_RATIO, 9)
  while (isFinite(reach) && reach > last && x.length < MAX_CURVE_POINTS) {
    next = next * GRID_RATIO
    x.push(next)
    last = next
  }
  // Two points is the minimum the backend accepts, and a one-point source that already reached its
  // ceiling would otherwise leave with one.
  if (x.length < 2) x.push(x[0] > 0 ? x[0] * GRID_RATIO : params.k)

  return { x: x, y: x.map(function (at) { return hillAt(params, at) }) }
}

// THE SOURCE'S OWN POINTS, WITH THE FIT'S TAIL PUT BACK ON THEM.
//
// A saturation curve stops where the business that was measured stopped buying, and responseAt reads
// a curve as FLAT past its last point — so an imported channel arrives claiming to cap out at
// whatever its source happened to explore, and says so nowhere. hillCurvePoints already fixes this
// for an EDITED curve, and for the same reason: past its last point a curve reads as flat on both
// sides of the wire. This does it without replacing anything measured.
//
// Every source point is kept exactly as it was — a fit inside HILL_FIT_TOLERANCE is close to them
// but it is not them, and what was measured is the better answer where there is one. What is added
// is a ladder past the last of them, sampled off the fit, out to where the curve reaches its ceiling.
//
// `mustReach` is put on the ladder as a point of its own when it falls past the measurements. It is
// the spend a placement is defined through, and a curve that does not carry the point its own
// placement names cannot express the statement the user made about it: responseAt returns a chord
// between neighbours anywhere else, and the whole arithmetic hangs on that one reading being the
// curve's own value rather than a linear approximation to it.
//
// The curve unchanged when the fit is not one to extrapolate from, which is the caller's judgement
// to make and not this function's.
export function curveWithFittedTail (params, curve, mustReach) {
  if (!isHillParams(params)) return curve
  const x = []
  const y = []
  const source = curve && Array.isArray(curve.x) ? curve.x : []
  const values = curve && Array.isArray(curve.y) ? curve.y : []
  for (let i = 0; i < Math.min(source.length, values.length); i++) {
    const at = Number(source[i])
    const value = Number(values[i])
    if (!isFinite(at) || at < 0 || !isFinite(value)) continue
    if (x.length > 0 && at <= x[x.length - 1]) continue
    x.push(at)
    y.push(value)
  }
  if (x.length === 0) return curve

  const reach = Math.max(ceilingPoint(params, CEILING_TARGET) * 1.2, Number(mustReach) || 0)
  const added = []
  let last = x[x.length - 1]
  let next = last > 0 ? last : params.k / Math.pow(GRID_RATIO, 9)
  while (isFinite(reach) && reach > last && x.length + added.length < MAX_CURVE_POINTS) {
    next = next * GRID_RATIO
    added.push(next)
    last = next
  }
  const wanted = Number(mustReach)
  if (isFinite(wanted) && wanted > x[x.length - 1]) added.push(wanted)
  added.sort(function (a, b) { return a - b }).forEach(function (at) {
    if (at <= x[x.length - 1]) return
    x.push(at)
    y.push(hillAt(params, at))
  })
  return Object.assign({}, curve, { x: x, y: y })
}

// A starting point that never fails, for when the fit does: the ceiling just above the highest
// response observed, the knee where the curve crosses half of it, and a mildly S-shaped exponent.
function heuristicHill (pairs) {
  const sorted = (pairs || []).filter(function (pair) {
    return pair && isFinite(pair[0]) && isFinite(pair[1])
  }).sort(function (a, b) { return a[0] - b[0] })

  let maxY = 0
  sorted.forEach(function (pair) { if (pair[1] > maxY) maxY = pair[1] })
  const vmax = maxY > 0 ? maxY * 1.05 : 1

  const half = vmax / 2
  let k = null
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i][1] >= half) {
      if (i === 0) {
        k = sorted[i][0]
      } else {
        const x0 = sorted[i - 1][0]
        const y0 = sorted[i - 1][1]
        const x1 = sorted[i][0]
        const y1 = sorted[i][1]
        k = y1 === y0 ? x1 : x0 + (x1 - x0) * ((half - y0) / (y1 - y0))
      }
      break
    }
  }
  if (!k || !isFinite(k) || k <= 0) {
    const positive = sorted.map(function (pair) { return pair[0] }).filter(function (x) { return x > 0 })
    k = positive.length > 0 ? positive[Math.floor(positive.length / 2)] : 1
  }

  return { vmax: vmax, k: k, n: 1.5 }
}

// Levenberg-Marquardt through the mixin's fitter, with the residual expressed against the largest
// response the curve was observed to produce, so `rmsePct` can be compared against
// HILL_FIT_TOLERANCE whatever the channel is worth.
//
// The observed response and not the fitted ceiling: a fit that cannot follow the data tends to
// escape by pushing Vmax far above anything measured and running a near-power-law through the
// middle of it, and dividing by that inflated ceiling would report the misfit as a rounding error.
// A two-knee curve fitted this way came back at 0.75% against its own ceiling and 8.9% against
// what the channel actually returned; the second number is the true one.
//
// `ok` is false when the fit could not be trusted; the parameters are still usable, they just came
// from the heuristic instead.
export function fitHill (pairs) {
  const usable = (pairs || []).filter(function (pair) {
    return pair && isFinite(pair[0]) && isFinite(pair[1]) && pair[0] >= 0
  })
  const fallback = function () {
    const guess = heuristicHill(usable)
    return { vmax: guess.vmax, k: guess.k, n: guess.n, rmse: Infinity, rmsePct: Infinity, ok: false }
  }
  // Three parameters cannot be identified from three points, and the wizard would rather show a
  // heuristic it can explain than a fit that went through every point it was given.
  if (usable.length < 4) return fallback()

  const result = fitSaturationCurve(usable)
  if (!result || !result.params || !result.params.every(isFinite) || !isFinite(result.rmse)) {
    return fallback()
  }
  const params = { vmax: result.params[0], k: result.params[1], n: result.params[2] }
  if (!isHillParams(params)) return fallback()

  let observed = 0
  usable.forEach(function (pair) { if (pair[1] > observed) observed = pair[1] })
  const reference = observed > 0 ? observed : params.vmax

  return {
    vmax: params.vmax,
    k: params.k,
    n: params.n,
    rmse: result.rmse,
    rmsePct: 100 * result.rmse / reference,
    ok: true
  }
}

// Resizing a channel to another model's scale stretches both axes: a business ten times larger
// spends ten times as much for ten times the revenue. Under this parameterisation that is exactly
// Vmax and K multiplied, with the exponent — which carries the shape, not the size — untouched.
//
// The two axes take separate multipliers because they are not always the same number. A resize is
// one factor on both, and passing one is still what that means; a CHANGE OF UNITS is two, since the
// spend axis and the response axis can be denominated in different currencies and only one of them
// may need converting. K lives on the spend axis and Vmax on the response axis, so each takes its
// own; the exponent is dimensionless and takes neither. `revenueFactor` defaults to `factor`, which
// is the resize every existing caller means.
export function scaleHill (params, factor, revenueFactor) {
  const multiplier = isFinite(factor) && factor > 0 ? factor : 1
  const response = isFinite(revenueFactor) && revenueFactor > 0 ? revenueFactor : multiplier
  return {
    vmax: params.vmax * response,
    k: params.k * multiplier,
    n: params.n
  }
}

// A step that lands on readable numbers: roughly a hundredth of the range, rounded down to 1, 2 or
// 5 times a power of ten.
export function niceStep (range) {
  if (!isFinite(range) || range <= 0) return 1
  const raw = range / 100
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const normalised = raw / magnitude
  let factor = 1
  if (normalised >= 5) {
    factor = 5
  } else if (normalised >= 2) {
    factor = 2
  }
  return factor * magnitude
}

// THE FLOOR UNDER THE EXPONENT, and the one slider bound here that is not a matter of taste.
//
// Below one a Hill has no interior peak: the derivative and the return per dollar are each greatest
// as the spend approaches nothing, so there is no threshold below which the channel does not pay,
// no peak marginal return and no peak return per dollar. The five optimal zones every screen in
// this wizard is drawn in terms of are not merely narrow there, they are UNDEFINED — hillRegions
// answers null, and the chart's shading, the bar under it and its five figures leave the screen
// together. A lever that can turn off the vocabulary of the screen it stands on stops before it.
//
// A margin above one rather than one exactly, a shape that far down being one nobody means to ask
// for: the first three zones at n = 1.001 come to five ten-thousandths of the half-saturation
// spend between them. Anything the exponent could reach from here still has all four boundaries,
// which is what the bar and the drag on it need to go on existing.
//
// A fit that arrived BELOW the floor keeps its own thumb on its own track, the ranges all widening
// to hold what is currently held — an imported shape is a measurement and this is a lever's range,
// and the two are not the same statement. Such a channel has no zones to begin with and the editor
// says so where the bar would have been.
const N_FLOOR = 1.05

// The four slider ranges, in one place so the component and its harness cannot disagree.
//
// The saturation point is bounded by the band validateEkrmForm checks — half to double the largest
// monthly spend — so dragging it can never produce the warning that the grid does not cover the
// spend the plan plans. The half-saturation spend is anchored to the knee the channel arrived with,
// a tenth of it to three times it, and not to the saturation point: a grid placed on the curve now
// ends dozens of knees out, so a range read off the point would leave every imported channel in the
// bottom hundredth of its own slider. Every range is widened to contain the value currently held,
// so a number typed into the field beside a slider does not silently snap back.
export function sliderBounds (options) {
  const opts = options || {}
  const reference = isHillParams(opts.reference) ? opts.reference : { vmax: 1, k: 1, n: 1.5 }
  const current = isHillParams(opts.current) ? opts.current : reference
  const point = isFinite(opts.point) && opts.point > 0 ? opts.point : 1
  const maxSpend = isFinite(opts.maxSpend) && opts.maxSpend > 0 ? opts.maxSpend : 0

  const range = function (min, max, value, step) {
    const low = Math.min(min, value)
    const high = Math.max(max, value)
    return { min: low, max: high, step: step || niceStep(high - low) }
  }

  return {
    point: range(
      maxSpend > 0 ? maxSpend / 2 : point / 4,
      maxSpend > 0 ? 2 * maxSpend : 4 * point,
      point
    ),
    vmax: range(reference.vmax * 0.25, reference.vmax * 3, current.vmax),
    k: range(reference.k * 0.1, reference.k * 3, current.k),
    n: range(N_FLOOR, 5, current.n, 0.05)
  }
}
